import { useEffect, useMemo, useState } from 'react';

import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { familyRelationsApi } from '../../family-relations/familyRelationsApi';
import type { FamilyRelation } from '../../family-relations/familyRelationTypes';
import { personnelApi, personsApi } from '../personnelApi';
import type { PersonnelFamilyMember, PersonnelFamilySuggestions } from '../personnelTypes';
import { sampleFamilyMember } from '../utils/sampleFamilyMember';

/**
 * Records or corrects one family member — CS Form 212, section II.
 *
 * **Editing loads the name from the identity store, and that is the split showing through.**
 * The row carries a `personId` and no name, so a form that edits the name has to ask the
 * identity service for it — one record, `personnel.reveal`, audited. There is no way to put
 * those fields on the row instead without copying names into `rsys_`.
 *
 * A reader without `personnel.reveal` therefore cannot edit a relative's name. That is the
 * correct outcome rather than a gap: the key exists to separate "may see the record" from "may
 * see and change who someone is".
 *
 * **The Relation picker is a CATALOG, not an enum.** Which further questions this form asks —
 * a date of birth, the occupation block — comes off the chosen row's flags rather than off a
 * hard-coded value, so a relation an administrator adds behaves like one the seed wrote. The
 * picker can also create: a reader who needs "Guardian" should not have to leave for a settings
 * page and come back to a form they have to fill again.
 */
export function EditFamilyMemberModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds a member. */
  editing: PersonnelFamilyMember | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [relationId, setRelationId] = useState(editing?.relationId ?? '');
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  const [occupation, setOccupation] = useState(editing?.occupation ?? '');
  const [employerName, setEmployerName] = useState(editing?.employerName ?? '');
  const [businessAddress, setBusinessAddress] = useState(editing?.businessAddress ?? '');
  const [telephoneNumber, setTelephoneNumber] = useState(editing?.telephoneNumber ?? '');

  /*
    The relation vocabulary. Fetched once when the modal opens: it is small, it does not change
    while the form is open, and every conditional below reads the chosen row out of it.
  */
  const [relations, setRelations] = useState<FamilyRelation[]>([]);
  const [relationsFailed, setRelationsFailed] = useState(false);

  /*
    What has been typed before. Fetched once when the modal opens rather than on every
    keystroke: the list is small, it does not change while the form is open, and a
    query-per-character would be a request per letter for a field that accepts anything.
  */
  const [suggestions, setSuggestions] = useState<PersonnelFamilySuggestions>({
    occupations: [],
    employers: [],
  });

  const [loading, setLoading] = useState(editing !== null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    Hide, don't disable. A reader who cannot create a relation still gets the picker; what they
    do not get is the "Add …" affordance for a value that is not in it, which would otherwise be
    a control that always 403s.
  */
  const canCreateRelation = usePermission('family-relations.create');

  useEffect(() => {
    const controller = new AbortController();

    familyRelationsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setRelations(next);
      })
      .catch(() => {
        /*
          NOT swallowed the way the suggestions below are, and the difference is that the form
          cannot be saved without a relation. An empty picker with no explanation reads as a
          feature that is broken; the flag turns it into a sentence on the field.
        */
        if (!controller.signal.aborted) setRelationsFailed(true);
      });

    personnelApi
      .familySuggestions(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setSuggestions(next);
      })
      .catch(() => {
        /*
          Swallowed on purpose, and this is the one place in the form where that is right:
          these are SUGGESTIONS. A failed fetch leaves the dropdowns empty and the reader
          types the value, which is what they would have done anyway — surfacing an error
          banner would report a problem that costs them nothing.
        */
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (editing === null) return;

    const controller = new AbortController();
    setLoading(true);

    // The name comes from the OTHER system. Two stores, composed here, because no single
    // endpoint can read across the seam.
    personsApi
      .reveal(editing.personId, controller.signal)
      .then((identity) => {
        if (controller.signal.aborted) return;

        setSalutation(identity.salutation ?? '');
        setFirstName(identity.firstName);
        setMiddleName(identity.middleName ?? '');
        setLastName(identity.lastName);
        setSuffix(identity.suffix ?? '');
        setDateOfBirth(identity.dateOfBirth ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this name.', 0),
        );

        // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [editing]);

  /*
    The chosen row, and every conditional in the form reads it.

    Derived rather than held in state: it is a function of `relationId` and `relations`, so
    storing it would leave a stale answer on screen for a paint after the picker changed — and
    the form would ask for a date of birth the relation no longer requires.
  */
  const relation = useMemo(
    () => relations.find((r) => r.id === relationId) ?? null,
    [relations, relationId],
  );

  /*
    A member recorded at a relation that has since been deleted keeps it, and the picker's feed
    is the ACTIVE rows — so the chosen value would resolve to nothing and the field would show
    its placeholder over a record that plainly has a relation. Injected as an option so the
    field states what the record says, exactly as a locked parent picker does.
  */
  const options = useMemo(() => {
    const live = relations.map((r) => ({
      value: r.id,
      label: r.name,
      sublabel: r.description ?? undefined,
    }));

    if (editing && !relations.some((r) => r.id === editing.relationId)) {
      return [
        ...live,
        { value: editing.relationId, label: editing.relationName, sublabel: 'No longer offered' },
      ];
    }

    return live;
  }, [relations, editing]);

  // The form's own rule, and it is the RELATION's to set rather than this form's.
  const needsBirthDate = (relation?.requiresDateOfBirth ?? false) && dateOfBirth === '';

  const valid =
    relationId !== '' &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    !needsBirthDate;

  const canSave = valid && confirmed && !saving && !loading;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      relationId,
      salutation: salutation.trim() ? salutation.trim() : null,
      firstName: firstName.trim(),
      middleName: middleName.trim() || null,
      lastName: lastName.trim(),
      suffix: suffix.trim() || null,
      dateOfBirth: dateOfBirth || null,
      occupation: occupation.trim() || null,
      employerName: employerName.trim() || null,
      businessAddress: businessAddress.trim() || null,
      telephoneNumber: telephoneNumber.trim() || null,
    };

    try {
      if (editing) await personnelApi.updateFamilyMember(personnelId, editing.id, request);
      else await personnelApi.addFamilyMember(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'family' });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Adds a relation from the picker, and selects it.
   *
   * Idempotent on the server, so a name that already exists comes back as the row that holds
   * it rather than as a rejection — which is what makes this safe to offer on a form two
   * readers can be filling at once.
   */
  async function createRelation(raw: string) {
    const name = raw.trim();
    if (!name) return;

    const created = await familyRelationsApi.create({
      name,
      description: null,
      guidance: null,

      /*
        The same defaults the create form starts from: a relation nobody has said anything about
        asks the ordinary questions, which here means a name and nothing else compulsory.
        Several-per-record is on, because a cap is the surprising answer and the one that turns
        into "This record already has a …" for a reader who never chose it.
      */
      allowsMultiple: true,
      requiresDateOfBirth: false,
      collectsOccupation: false,
    });

    setRelations((current) =>
      current.some((r) => r.id === created.id) ? current : [...current, created],
    );

    setRelationId(created.id);
  }

  function generate() {
    /*
      The CATALOG goes in, not just the chosen row.

      The relation is a required field, so a generator that did not fill one would leave Save
      disabled — the silent breakage Autogenerate exists to prevent. It resolves against the
      rows the form actually loaded rather than inventing an id the server would refuse, and it
      keeps the reader's own choice when they have made one.
    */
    const sample = sampleFamilyMember(relations, relation);

    setRelationId(sample.relationId);
    setSalutation(sample.salutation);
    setFirstName(sample.firstName);
    setMiddleName(sample.middleName);
    setLastName(sample.lastName);
    setSuffix(sample.suffix);
    setDateOfBirth(sample.dateOfBirth);
    setOccupation(sample.occupation);
    setEmployerName(sample.employerName);
    setBusinessAddress(sample.businessAddress);
    setTelephoneNumber(sample.telephoneNumber);
  }

  // Asked only where the relation says so, so the fields are not drawn for anyone else — a
  // control that can never apply is noise, and the form does not ask it.
  const collectsOccupation = relation?.collectsOccupation ?? false;

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit family member' : 'Add family member'}
      size="2xl"
      id="family-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'family-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving || loading,
      }}
      footer={
        <>
          <Button id="family-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="family-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/*
              The name is one row of three, as it is on the personnel form: first, middle, last.
              The form prints it surname-first — "surname, first name, name extension, middle
              name" — but the FIELDS are named for what they hold, and the surname-first display
              is the identity service's single decision. A form that reordered its own inputs to
              match a printed layout would be asking the reader to think in the form's order
              rather than their own.
            */}
            <div className="grid gap-4 sm:grid-cols-3">
              <FieldGroup
                label="First name"
                labelFor="family-first-name"
                description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
                segments={[
                  {
                    id: 'family-salutation',
                    label: 'Salutation',
                    value: salutation,
                    onChange: setSalutation,
                    options: SALUTATION_OPTIONS,
                    error: error?.fieldError('salutation'),
                    disabled: saving,
                    // The addon gives way, never the grid — a name row is three equal columns.
                    width: SALUTATION_SEGMENT_WIDTH,
                    emptyLabel: SALUTATION_EMPTY_LABEL,
                  },
                  {
                    id: 'family-first-name',
                    label: 'First name',
                    value: firstName,
                    onChange: setFirstName,
                    required: true,
                    error: firstName.trim() ? error?.fieldError('firstName') : 'Enter a first name.',
                    disabled: saving,
                  },
                ]}
              />

              <TextField
                id="family-middle-name"
                label="Middle name"
                description="Optional — plenty of records have none."
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                error={error?.fieldError('middleName')}
                disabled={saving}
              />

              <FieldGroup
                label="Last name"
                description="The narrow box is the form’s name extension — Jr., Sr., III."
                segments={[
                  {
                    id: 'family-last-name',
                    label: 'Last name',
                    value: lastName,
                    onChange: setLastName,
                    required: true,
                    error: lastName.trim() ? error?.fieldError('lastName') : 'Enter a surname.',
                    disabled: saving,
                  },
                  {
                    id: 'family-suffix',
                    label: 'Suffix',
                    value: suffix,
                    onChange: setSuffix,
                    error: error?.fieldError('suffix'),
                    disabled: saving,
                    width: 'w-20 shrink-0',
                  },
                ]}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id="family-relation"
                label="Relation"
                required
                description="Maintained under Personnel → Settings → Family Relations. Type a name that is not listed to add it."
                /*
                  `allowCustom` with `onCreateOption`: an unmatched query offers Add "…" and the
                  handler writes the catalog row under its own key before selecting it. Withheld
                  from a reader without that key — hide, don't disable.
                */
                allowCustom={canCreateRelation}
                onCreateOption={canCreateRelation ? createRelation : undefined}
                value={relationId || null}
                onChange={(next) => setRelationId(next ?? '')}
                options={options}
                // The catalog's own order IS the form's order, so the picker must not re-sort it.
                sort="none"
                clearable={false}
                error={
                  relationsFailed
                    ? 'The relations could not be loaded. Close this and try again.'
                    : relationId
                      ? error?.fieldError('relationId')
                      : 'Choose a relation.'
                }
                disabled={saving}
              />

              <DateField
                id="family-date-of-birth"
                label="Date of birth"
                required={relation?.requiresDateOfBirth ?? false}
                showAge
                max={todayCalendarDate()}
                /*
                  Whether it is compulsory is the relation's to say. It is offered either way
                  because the identity store can hold one and a record that has it should not
                  have to throw it away.
                */
                description={
                  relation?.requiresDateOfBirth
                    ? `The form asks for every ${relation.name.toLowerCase()}’s date of birth.`
                    : 'Optional for this relation.'
                }
                value={dateOfBirth}
                onChange={setDateOfBirth}
                error={
                  needsBirthDate ? 'Enter the date of birth.' : error?.fieldError('dateOfBirth')
                }
                disabled={saving}
              />
            </div>

            {/*
              The relation's own guidance, rendered once.

              It was two hard-coded paragraphs about a mother's maiden name — one as the middle
              name field's help text and one standing under the row — both keyed on the enum
              value 3. It is a column on the catalog row now, so a relation an administrator adds
              can carry its own instruction and nothing here has to know which relation it is.
            */}
            {relation?.guidance ? (
              <p className="text-xs text-slate-500">{relation.guidance}</p>
            ) : null}

            {collectsOccupation ? (
              <FormFieldset legend="Occupational Info">
                {/*
                  FULL WIDTH, one each. These two are dropdowns and their values are long — a job
                  title and a company name — so at half a row the chosen value truncates in the
                  very control that exists to show it.
                */}
                <Select
                  id="family-occupation"
                  label="Occupation"
                  /*
                    `allowCustom` with no `onCreateOption`: an unmatched query offers Add "…"
                    and commits the raw text. That is the whole behaviour wanted here — there is
                    no catalog row to create, and the list is a record of what has been typed
                    rather than a vocabulary to add to.
                  */
                  allowCustom
                  description="Pick one already on file, or type a new one."
                  value={occupation || null}
                  onChange={(next) => setOccupation(next ?? '')}
                  options={suggestions.occupations.map((value) => ({
                    value,
                    label: value,
                  }))}
                  error={error?.fieldError('occupation')}
                  disabled={saving}
                />

                <Select
                  id="family-employer-name"
                  label="Employer or business name"
                  allowCustom
                  description="Pick one already on file, or type a new one."
                  value={employerName || null}
                  onChange={(next) => setEmployerName(next ?? '')}
                  options={suggestions.employers.map((value) => ({
                    value,
                    label: value,
                  }))}
                  error={error?.fieldError('employerName')}
                  disabled={saving}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    id="family-business-address"
                    label="Business address"
                    value={businessAddress}
                    onChange={(e) => setBusinessAddress(e.target.value)}
                    error={error?.fieldError('businessAddress')}
                    disabled={saving}
                  />

                  <TextField
                    id="family-telephone-number"
                    label="Telephone"
                    value={telephoneNumber}
                    onChange={(e) => setTelephoneNumber(e.target.value)}
                    error={error?.fieldError('telephoneNumber')}
                    disabled={saving}
                  />
                </div>
              </FormFieldset>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
